import CheckIcon from "@mui/icons-material/Check";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import { useState } from "react";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import LoadingButton from "_shared/_views/loading/loading-button";
import EmailAddressInput from "_shared/email-addresses/_views/email-address-input";
import EmailAddress from "_shared/email-addresses/email-address";
import NameInput from "_shared/names/_views/name-input";
import Name from "_shared/names/name";
import RegistrationInfo, { InvalidRegistrationInfoError } from "legal-entities/registration-info/registration-info";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";
import { useAcceptOfficerInvitationMutation } from "legal-entities/invitations/officer-invitations/_api/entity-officer-invitation-api-service";
import EntityOfficerInvitation, {
  InvalidEntityOfficerInvitationError,
} from "legal-entities/invitations/officer-invitations/entity-officer-invitation";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import PasswordInput, { PasswordIssues } from "users/auth/credentials/passwords/_views/password-input";
import Password from "users/auth/credentials/passwords/password";
import OfficerOnboardingProgress from "legal-entities/onboarding/_views/officer-onboarding-progress";

const InvitationForm = styled(Container)(({ theme }) => ({
  color: theme.palette.text.primary,
  maxWidth: 720,
  textAlign: "left",
  overflowWrap: "anywhere",
}));
const AccountCard = styled(Paper)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(4),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));
const FormContainer = styled("form")(({ theme }) => ({
  marginTop: theme.spacing(3),
}));
const FormElements = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
}));
const InvitationFormTitle = styled("h2")(({ theme }) => ({
  ...theme.typography.h5,
  margin: theme.spacing(0, 0, 1),
}));
const InvitationFormInstructions = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
  "& a": {
    color: theme.palette.primary.main,
    textDecoration: "underline",
  },
}));
const InvitationMessage = styled("blockquote")(({ theme }) => ({
  ...theme.typography.body2,
  backgroundColor: theme.palette.grey[50],
  borderLeft: `3px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.secondary,
  margin: theme.spacing(2, 0),
  padding: theme.spacing(2),
}));
const InvitationEmailInput = styled(EmailAddressInput)(() => ({
  width: "100%",
  "&:first-of-type": {
    marginRight: 0,
  },
}));
const InvitationNameInput = styled(NameInput)(() => ({
  flex: 1,
  minWidth: 0,
}));
const InvitationPasswordInput = styled(PasswordInput)(({ theme }) => ({
  minWidth: 0,
  width: `calc((100% - ${theme.spacing(1.5)}) / 2)`,
  [theme.breakpoints.down("md")]: {
    width: "100%",
    "&:first-of-type": {
      marginRight: 0,
    },
  },
}));
const InvitationAcceptButton = styled(LoadingButton)(({ theme }) => ({
  marginTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

type InvitationAcceptanceFormError = string | PasswordIssues;

type InvitationAcceptanceFormErrors = {
  general?: string;
  email?: string;
  name?: string;
  password?: PasswordIssues;
};

type EntityOfficerInvitationAcceptanceFormProps = {
  className?: string;
  invitation: EntityOfficerInvitation;
};

export default function EntityOfficerInvitationAcceptanceForm(
  props: Readonly<EntityOfficerInvitationAcceptanceFormProps>,
) {
  const { invitation, className } = props;

  const [invitationEmail, setInvitationEmail] = useState<EmailAddress>(invitation.registrationInfo.email);
  const [invitationName, setInvitationName] = useState<Name>(invitation.registrationInfo.name);
  const [invitationPassword, setInvitationPassword] = useState<Password>();
  const [formErrors, setFormErrors] = useState<InvitationAcceptanceFormErrors>();
  const [saving, setSaving] = useState<boolean>(false);

  const session = useSession();
  const navigate = useNavigate();
  const [acceptOfficerInvitation] = useAcceptOfficerInvitationMutation();

  function rebuildInvitation(
    emailAddress?: EmailAddress,
    name?: Name,
    password?: Password,
    errors?: InvitationAcceptanceFormErrors,
  ): EntityOfficerInvitation | undefined {
    if (!invitation || !formValid(errors) || !emailAddress || !name || !password) return;

    try {
      const invitationRegistrationInfo = RegistrationInfo.create({ params: { name, email: emailAddress, password } });
      const updatedInvitation = EntityOfficerInvitation.create({
        params: {
          entityName: invitation.entityName,
          entityLocation: invitation.entityLocation,
          title: invitation.title,
          registrationInfo: invitationRegistrationInfo,
          message: invitation.message,
          id: invitation.id,
          status: invitation.status,
        },
      });
      return updatedInvitation;
    } catch (error) {
      if (error instanceof InvalidEntityOfficerInvitationError || error instanceof InvalidRegistrationInfoError) {
        let errors: InvitationAcceptanceFormErrors = {};
        errors.general = error.message;
        setFormErrors(errors);
        return;
      }
      throw error;
    }
  }

  async function handleAcceptInvitation() {
    setSaving(true);

    const builtInvite = rebuildInvitation(invitationEmail, invitationName, invitationPassword);

    try {
      if (!builtInvite) throw new InvalidEntityOfficerInvitationError("Invitation is invalid");
      const entityAgreement = await acceptOfficerInvitation({ invitation: builtInvite, session }).unwrap();

      navigate(`/entity-agreements/${entityAgreement.id}`, {
        state: { entityAgreement: entityAgreement },
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar((error as Error)?.message ?? "Error accepting invite", { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  function formValid(errors?: InvitationAcceptanceFormErrors): boolean {
    errors ??= formErrors;

    return (
      invitationEmail !== undefined &&
      invitationName !== undefined &&
      invitationPassword !== undefined &&
      !errors?.general &&
      !errors?.email &&
      !errors?.name &&
      !errors?.password
    );
  }

  function updateFormErrors(type: "email" | "name" | "password", error?: InvitationAcceptanceFormError) {
    const errors: InvitationAcceptanceFormErrors = { ...formErrors };

    if (type === "password") {
      errors.password = error as PasswordIssues;
    } else {
      errors[type] = error as string | undefined;
    }

    setFormErrors(errors);
    return errors;
  }

  function invitationAlreadyAcceptedMessage() {
    return (
      <InvitationForm className={className} maxWidth={false} disableGutters>
        <AccountCard elevation={0}>
          <InvitationFormTitle>Invitation Already Accepted</InvitationFormTitle>
          <InvitationFormInstructions>
            The invitation has already been accepted.
            <br />
            If you believe this is in error please contact{" "}
            <a href="mailto:support@attorneyhub.com" target="_blank" rel="noreferrer">
              support@attorneyhub
            </a>
            .
          </InvitationFormInstructions>
          <InvitationFormInstructions>You can close this tab</InvitationFormInstructions>
        </AccountCard>
      </InvitationForm>
    );
  }

  if (!invitation) return null;
  if (invitation.status === InvitationStatus.Accepted) return invitationAlreadyAcceptedMessage();

  return (
    <InvitationForm className={className} maxWidth={false} disableGutters>
      <OfficerOnboardingProgress activeStep={0} organizationName={invitation.entityName.value} />
      <AccountCard elevation={0}>
        <InvitationFormTitle>Create your account</InvitationFormTitle>
        <InvitationFormInstructions>
          You were invited to set up {invitation.entityName.value}
          {invitation.title?.value ? ` as ${invitation.title.value}` : " as its officer"}. Confirm your details and choose
          a password to continue.
        </InvitationFormInstructions>
        {invitation.message?.value && (
          <InvitationMessage>&ldquo;{invitation.message.value}&rdquo;</InvitationMessage>
        )}
        <FormContainer>
          <InvitationEmailInput
            idPrefix="officer-invitation-acceptance"
            variant="outlined"
            initialValue={invitationEmail}
            onChange={(updatedEmailAddress?: EmailAddress, error?: string) => {
              if (updatedEmailAddress) setInvitationEmail(updatedEmailAddress);
              const errors = updateFormErrors("email", error);
              rebuildInvitation(updatedEmailAddress, invitationName, invitationPassword, errors);
            }}
          />
          <FormElements>
            <InvitationNameInput
              idPrefix="officer-invitation-acceptance"
              variant="outlined"
              initialValue={invitationName}
              onChange={(updatedName?: Name, error?: string) => {
                if (updatedName) setInvitationName(updatedName);
                const errors = updateFormErrors("name", error);
                rebuildInvitation(invitationEmail, updatedName, invitationPassword, errors);
              }}
            />
            <InvitationPasswordInput
              idPrefix="officer-invitation-acceptance"
              variant="outlined"
              confirm={true}
              password={invitationPassword}
              onChange={(updatedPassword?: Password, error?: PasswordIssues) => {
                if (updatedPassword) setInvitationPassword(updatedPassword);
                const errors = updateFormErrors("password", error);
                rebuildInvitation(invitationEmail, invitationName, updatedPassword, errors);
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key === KeyboardKey.Enter && formValid()) {
                  void handleAcceptInvitation();
                }
              }}
            />
          </FormElements>
          <InvitationAcceptButton
            id="officer-invitation-acceptance-submit-button"
            variant="contained"
            color="primary"
            disabled={saving || !formValid()}
            loading={saving}
            startIcon={<CheckIcon />}
            onClick={handleAcceptInvitation}
          >
            Create Account and Continue
          </InvitationAcceptButton>
        </FormContainer>
      </AccountCard>
    </InvitationForm>
  );
}
